<template>
  <div
    class="chat-box__wrapper"
    :class="[classes, {'dark': isDarkTheme, 'cursor-default': emptyContentHTML}]"
    :style="{width}"
  >
    <!-- Header -->
    <div class="chat-box__header">
      <aha-icon
        v-if="canClose"
        class="chat-box__header-close-icon"
        name="close"
        @click="closeChatBox()"
        width="18"
        height="18"
        viewBox="0 0 24 24"
      />
    </div>

    <!-- Content -->
    <div
      class="chat-box__chats-container"
      ref="chat-box__chats-container"
      id="chat-box__chats-container"
      :style="{ height: chatsHeight }"
    >
      <div class="sentinel" id="chat-box__sentinel" />
      <div v-if="canLoadMore" class="chat-box__load-more">
        <aha-spinner />
      </div>

      <aha-chat-content
        @retrySendingMessage="retry"
        v-if="chatMessages && chatMessages.length > 0"
        :chats="visibleMessages"
        :filteringProfanity="filteringProfanity"
      />
      <div
        v-else
        class="chat-box__empty"
        v-html="emptyContentHTML"
      />
      <!-- <div
        v-if="isShowScrollToBottom"
        class="chat-box__scroll-to-bottom"
        v-tooltip="'See new messages'"
      >
        <span @click="scrollToBottom">
          <i class="fa fa-arrow-down" />
        </span>
      </div> -->
    </div>
    <!-- Input -->
    <div class="chat-box__input-container">
      <input
        ref="chat-box__input"
        type="text"
        :placeholder="$t('enterYourMessage')"
        class="chat-box__input"
        v-model="message"
        @keyup.enter="handleSendMessage()"
        @focus="handleFocusInput()"
        @blur="handleOutFocusInput()"
        :maxlength="MAX_MESSAGE_CHARS"
        :disabled="disableSendMessage"
      >
      <span
        class="chat-box__input-count"
        v-if="characterCounterVisible"
      >
        {{ characterCounter }}
      </span>
      <aha-icon
        name="send"
        height="20"
        width="20"
        viewBox="0 0 24 24"
        @click="handleSendMessage()"
        class="chat-box__input-send"
        :class="{'active': isMessageInputActive}"
      />
    </div>
  </div>
</template>
<script>
import _ from 'lodash'
import AhaChatContent from './ChatContent.vue'

const MAX_MESSAGE_CHARS = 200

export default {
  components: {
    AhaChatContent,
  },
  props: {
    classes: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      default: '',
    },
    chatsHeight: {
      type: String,
      default: '240px',
    },
    totalLiveParticipants: {
      type: Number,
      default: 0,
    }, // for v2 of chat
    chatMessages: {
      type: Array,
      default: () => [],
    },
    filteringProfanity: {
      type: Boolean,
      default: false,
    },
    isDarkTheme: {
      type: Boolean,
      default: false,
    },
    canClose: {
      type: Boolean,
      default: true,
    },
    emptyContentHTML: {
      type: String,
      default: '',
    },
    userId: {
      type: String,
      default: null,
    },
  },
  data: () => ({
    message: '',
    characterCounterVisible: false,
    isMessageInputActive: false,
    page: 0,
    pageCount: 30,
    canLoadMore: false,
    isLoadingMore: false,
    visibleMessages: [],
    initialLoad: false,
    isShowScrollToBottom: false,
    disableSendMessage: false,
    MAX_MESSAGE_CHARS,
  }),

  computed: {
    characterCounter() {
      if (this.message && this.message.length > MAX_MESSAGE_CHARS) {
        return 0
      }
      return this.message ? MAX_MESSAGE_CHARS - this.message.length : MAX_MESSAGE_CHARS
    },
  },

  watch: {
    chatMessages: function watchChatMessages(chatMessages) {
      this.setVisibleMessages(chatMessages)
      if (chatMessages && chatMessages.length > 0) {
        const lastestMessage = chatMessages[chatMessages.length - 1]
        if (this.canScrollToBottom(lastestMessage)) {
          this.scrollToBottom()
        }
      }
    },

    message(value) {
      if (!value || !value.trim().length) {
        this.isMessageInputActive = false
      }
      if (this.isMessageInputActive) return
      if (value && value.trim().length) {
        this.isMessageInputActive = true
      }
    },
  },

  mounted() {
    this.setVisibleMessages(this.chatMessages)
    this.setUpInterSectionObserver()
    this.scrollToBottom()
    const elem = this.getChatContainer()
    elem.addEventListener('scroll', _.throttle(() => {
      this.recordScrollPosition()
    }, 300))
  },

  destroyed() {
    if (this.listEndObserver) {
      this.listEndObserver.disconnect()
    }
  },

  methods: {
    canScrollToBottom(latestMessage) {
      if (`${latestMessage.userId}` === `${this.userId}`) {
        return true
      }
      if (this.previousScrollHeightMinusScrollTop > 210) {
        return false
      }
      return true
    },
    setUpInterSectionObserver() {
      if (this.listEndObserver) {
        this.listEndObserver.disconnect()
      }
      const options = {
        root: this.getChatContainer(),
        rootMargin: '50px',
      }
      this.listEndObserver = new IntersectionObserver(
        this.handleIntersection,
        options,
      )
      this.listEndObserver.observe(this.getSentinel())
    },
    handleIntersection([entry]) {
      if (entry.isIntersecting && this.canLoadMore && !this.isLoadingMore) {
        this.loadMore()
      }
    },
    setVisibleMessages(allMessages) {
      if (allMessages && allMessages.length > 0) {
        this.canLoadMore = allMessages && allMessages.length > this.pageCount
        this.visibleMessages = allMessages.slice(-this.pageCount)
        this.totalPage = Math.ceil(allMessages.length / this.pageCount)
      }
    },
    closeChatBox() {
      this.$parent.toggleChatBox()
    },

    scrollToBottom() {
      this.page = 0

      this.$nextTick(() => {
        const chatMessagesContainer = this.getChatContainer()
        if (chatMessagesContainer) {
          chatMessagesContainer.scrollTop = chatMessagesContainer.scrollHeight
        }
        this.recordScrollPosition()
      })
      this.isShowScrollToBottom = false
    },

    handleSendMessage() {
      if (this.message && this.message.trim().length) {
        this.$emit('sendMessage', this.message)
        this.message = ''
        // Throttle sending message on client
        this.disableSendMessage = true
        setTimeout(() => {
          this.disableSendMessage = false
          this.$nextTick(this.handleFocusInput)
        }, 1000)
      }
    },

    handleFocusInput() {
      const input = this.$refs['chat-box__input']
      if (!input) return
      this.$emit('focusChatInput')
      this.characterCounterVisible = true
      input.focus()
      if (this.message && this.message.trim().length) {
        this.isMessageInputActive = true
      }
    },

    handleOutFocusInput() {
      this.$emit('blurChatInput')
      this.isMessageInputActive = false
      this.characterCounterVisible = false
    },

    getChatContainer() {
      const elem = document.getElementById('chat-box__chats-container')
      return elem
    },
    getSentinel() {
      const elem = document.getElementById('chat-box__sentinel')
      return elem
    },

    recordScrollPosition() {
      const container = this.getChatContainer()
      if (container) {
        this.previousScrollHeightMinusScrollTop = container.scrollHeight - container.scrollTop
      }
    },

    restoreScrollPosition() {
      const container = this.getChatContainer()
      if (container) {
        container.scrollTop = container.scrollHeight - this.previousScrollHeightMinusScrollTop
      }
    },

    loadMore() {
      this.isLoadingMore = true
      this.recordScrollPosition()

      this.page += 1
      if (this.page === this.totalPage - 1) {
        this.canLoadMore = false
      }

      setTimeout(() => {
        const prependedMsgs = this.chatMessages
          .slice(-this.pageCount * (this.page + 1), -this.pageCount * (this.page))
        this.visibleMessages.unshift(...prependedMsgs)

        this.isLoadingMore = false

        this.$nextTick(() => {
          this.restoreScrollPosition()
        })
      }, 600)
    },

    retry(message) {
      this.$emit('retrySendingMessage', message)
    },
  },
}
</script>

<style lang="scss" scoped>
.sentinel {
  height: 0px;
}

.chat-box__wrapper {
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 14px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0px 6px 11px rgba(0, 0, 0, 0.07);
  background-color: #ffffff;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  &.dark {
    background-color: rgba(0, 0, 0, 0.7);
    .chat-box__header {
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.3) 10.42%, rgba(0, 0, 0, 0) 100%);
    }
    .chat-box__input-container {
      box-shadow: 0px -1px 4px rgba(255, 255, 255, 0.04);
      background-color: #000000;
    }
    .chat-box__chats-container{
      &::-webkit-scrollbar {
        border-top-right-radius: 14px;
        background: rgba(0, 0, 0, 0.2);
      }
      /* Handle */
      &::-webkit-scrollbar-thumb {
        background: #000000;
        border-radius: 20px;
        width: 90%;
      }
    }
  }
}

.chat-box__header {
  padding: 0 12px;
  border-top-left-radius: 9999px;
  border-top-right-radius: 9999px;
  text-align: right;
  background: linear-gradient(180deg, #FFFFFF 10.42%, rgba(255, 255, 255, 0) 100%);
  opacity: 0.7;
  width: 100%;
  position: absolute;
  top: 0px;
  height: 12px;
  .chat-box__header-close-icon {
    cursor: pointer;
    color: #999999;
    font-size: 14px;
    &:hover {
      color: #707070;
    }
  }
}

.chat-box__load-more {
  text-align: center;
  margin-top: 8px;
}

.chat-box__input-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  margin-top: 5px;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 14px;
  box-shadow: 0px -1px 4px rgba(0, 0, 0, 0.04);
  .chat-box__input {
    flex: 1;
    font-size: 14px;
    border: none;
    outline: none;
    background-color: inherit;
    color: inherit;
  }
  .chat-box__input-count {
    font-size: 14px;
    color: #B4BCCF;
    padding-left: 0.75rem;
    padding-right: 0.75rem;
  }
  .chat-box__input-send {
    color: #D1D1D1;
    margin-bottom: 2px;
    cursor: default;
    &.active {
      color:  #348EF6;
      cursor: pointer;
    }
  }
  .chat-box__input-loading {
    text-align: right;
  }
}

.chat-box__chats-container {
  overflow-y: auto;
  overflow-x: hidden;
  &::-webkit-scrollbar {
    border-top-right-radius: 14px;
    background: rgba(255, 255, 255, 0.2);
  }
  /* Handle */
  &::-webkit-scrollbar-thumb {
    background: #EBEBEB;
    border-radius: 20px;
    width: 90%;
  }
}

.chat-box__scroll-to-bottom {
  position: absolute;
  bottom: 50px;
  right: 28px;
  font-size: 0.8rem;
  border-radius: 100%;
  background:#348EF6;
  width: 25px;
  height: 25px;
  display: flex;
  align-items: center;
  color: #fff;
  justify-content: center;
  cursor: pointer;
}

.chat-box__empty {
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 65%;
  text-align: center;
  margin: 0 auto;
  &::v-deep .chat-box__empty-heading {
    margin-top:0;
    font-weight: bold;
    padding-bottom: 0.5rem;
  }
  &::v-deep .chat-box__empty-text {
    margin-top: 0;
    font-size: 14px;
    color: #999999;
    line-height: 1.5;
  }
}
</style>
